import React from 'react';
import { BookOpen, CheckCircle2, FileText, FolderOpen, Loader2, Settings, Upload } from 'lucide-react';
import { getSavedLessonDocuments } from '../../utils/teacherResources';

const formatDate = (value) => {
    if (!value) return 'Recently updated';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Recently updated';

    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
};

const getDocumentTitle = (document = {}) => (
    document.title ||
    document.name ||
    document.filename ||
    'Untitled resource'
);

const getCourseNames = (operatingState = {}) => (
    (operatingState.courses || []).map((course) => course.name).filter(Boolean)
);

const getPrepItems = (operatingState = {}) => (
    (operatingState.aiPreparationQueue || []).slice(0, 4)
);

const ResourceList = ({ title, icon: Icon, items, emptyText }) => (
    <section className="rounded-2xl border border-white/80 bg-white/90 p-5 shadow-[0_10px_30px_rgba(15,23,42,0.05)] dark:border-slate-700/60 dark:bg-[#171d26]/95">
        <div className="mb-4 flex items-center gap-2">
            {React.createElement(Icon, { size: 17, className: 'text-amber-700 dark:text-amber-300' })}
            <h2 className="text-sm font-semibold text-slate-950 dark:text-white">{title}</h2>
            <span className="ml-auto rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                {items.length}
            </span>
        </div>

        {items.length === 0 ? (
            <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900/55 dark:text-slate-400">
                {emptyText}
            </p>
        ) : (
            <div className="space-y-2">
                {items.slice(0, 5).map((item) => (
                    <div
                        key={item.id || getDocumentTitle(item)}
                        className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-900/55"
                    >
                        <p className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">
                            {getDocumentTitle(item)}
                        </p>
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                            {formatDate(item.created_at || item.uploadedAt || item.updated_at)}
                        </p>
                    </div>
                ))}
            </div>
        )}
    </section>
);

const TeacherResourcesWorkspace = ({
    savedDocuments = [],
    contextDocuments = [],
    loading = false,
    error = null,
    operatingState = null,
    onOpenSavedLessons,
    onOpenAccountSettings,
}) => {
    const savedLessonDocuments = getSavedLessonDocuments(savedDocuments);
    const hasResources = savedLessonDocuments.length > 0 || contextDocuments.length > 0;
    const courseNames = getCourseNames(operatingState);
    const prepItems = getPrepItems(operatingState);

    return (
        <div className="space-y-5">
            <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                    Resources
                </p>
                <h1 className="mt-2 text-3xl font-semibold leading-tight text-slate-950 sm:text-4xl dark:text-white">
                    Teaching resource shelf
                </h1>
                <p className="mt-2 max-w-2xl text-sm text-slate-600 dark:text-slate-300">
                    A lightweight place for generated lessons, uploaded context, and saved teacher materials.
                </p>
            </div>

            <div className="grid gap-3 md:grid-cols-3">
                <div className="rounded-2xl border border-white/80 bg-white/90 p-4 dark:border-slate-700/60 dark:bg-[#171d26]/95">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Context coverage</p>
                    <p className="mt-2 text-2xl font-semibold text-slate-950 dark:text-white">{contextDocuments.length}</p>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        Uploaded profile documents available for planning context.
                    </p>
                </div>
                <div className="rounded-2xl border border-white/80 bg-white/90 p-4 dark:border-slate-700/60 dark:bg-[#171d26]/95">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Saved lessons</p>
                    <p className="mt-2 text-2xl font-semibold text-slate-950 dark:text-white">{savedLessonDocuments.length}</p>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        Generated lesson artifacts ready to reopen from My Documents.
                    </p>
                </div>
                <div className="rounded-2xl border border-white/80 bg-white/90 p-4 dark:border-slate-700/60 dark:bg-[#171d26]/95">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Active courses</p>
                    <p className="mt-2 text-2xl font-semibold text-slate-950 dark:text-white">{courseNames.length}</p>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        Course signals that should drive planning and resource choices.
                    </p>
                </div>
            </div>

            {loading && (
                <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900/55 dark:text-slate-300">
                    <Loader2 size={16} className="animate-spin" />
                    Loading resources...
                </div>
            )}

            {error && (
                <div className="rounded-2xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-400/20 dark:bg-amber-400/10 dark:text-amber-200">
                    {error}
                </div>
            )}

            {!loading && !hasResources && (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-white/80 p-6 dark:border-slate-700 dark:bg-slate-900/55">
                    <div className="flex items-start gap-3">
                        <FolderOpen size={30} className="mt-0.5 text-slate-300" />
                        <div>
                            <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                                No saved resources yet.
                            </p>
                            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                                This shelf still shows what to gather next: course context, reusable lesson artifacts, and teacher-owned materials that make tomorrow easier.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {(courseNames.length > 0 || prepItems.length > 0) && (
                <section className="rounded-2xl border border-white/80 bg-white/90 p-5 shadow-[0_10px_30px_rgba(15,23,42,0.05)] dark:border-slate-700/60 dark:bg-[#171d26]/95">
                    <div className="mb-4 flex items-center gap-2">
                        <CheckCircle2 size={17} className="text-amber-700 dark:text-amber-300" />
                        <h2 className="text-sm font-semibold text-slate-950 dark:text-white">Teacher resource plan</h2>
                    </div>
                    <div className="grid gap-3 lg:grid-cols-2">
                        <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/55">
                            <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Course context to keep current</p>
                            <div className="mt-3 flex flex-wrap gap-2">
                                {courseNames.length > 0 ? courseNames.map((name) => (
                                    <span key={name} className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700">
                                        {name}
                                    </span>
                                )) : (
                                    <span className="text-sm text-slate-500 dark:text-slate-400">Add courses in Settings to organize resources by class.</span>
                                )}
                            </div>
                        </div>
                        <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/55">
                            <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Reusable resources worth preparing</p>
                            {prepItems.length > 0 ? (
                                <ul className="mt-3 space-y-2">
                                    {prepItems.map((item) => (
                                        <li key={item.id || item.label} className="text-sm text-slate-700 dark:text-slate-200">
                                            <span className="font-semibold">{item.label}</span>
                                            <span className="text-slate-500 dark:text-slate-400"> - {item.output || item.reason}</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
                                    Generate a lesson or upload context to start building reusable packets.
                                </p>
                            )}
                        </div>
                    </div>
                </section>
            )}

            <div className="grid gap-4 lg:grid-cols-2">
                <ResourceList
                    title="Generated lesson materials"
                    icon={BookOpen}
                    items={savedLessonDocuments}
                    emptyText="Saved generated lessons will appear after you save lesson output."
                />
                <ResourceList
                    title="Uploaded teacher context"
                    icon={Upload}
                    items={contextDocuments}
                    emptyText="Uploaded context from Settings will appear here."
                />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
                <button
                    type="button"
                    onClick={onOpenSavedLessons}
                    className="flex items-center justify-center gap-2 rounded-xl bg-[#FFC107] px-4 py-3 text-sm font-semibold text-[#1d1d1f] transition-colors hover:bg-[#e6ad00]"
                >
                    <FileText size={16} />
                    Open My Documents
                </button>
                <button
                    type="button"
                    onClick={onOpenAccountSettings}
                    className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition-colors hover:border-slate-300 hover:text-slate-950 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:border-slate-500"
                >
                    <Settings size={16} />
                    Uploaded Context
                </button>
            </div>
        </div>
    );
};

export default TeacherResourcesWorkspace;
